import { filter, debounce } from 'underscore';
import hoistNonReactStatics from 'hoist-non-react-statics';
import React, { useState, useEffect, useMemo } from 'react';

export interface IWithEntitySearchProps {
  entityList: any[];
}

export type WithEntitySearch = <P extends IWithEntitySearchProps>(
  WrappedComponent: React.ComponentType<P>
) => React.ComponentType<P>;

const DEBOUNCE_TIME = 250;

export const withEntitySearch: WithEntitySearch = (WrappedComponent) => {
  const HOC = (props) => {
    const { entityList, ...rest } = props;
    const [searchText, setSearchText] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const filteredEntityList = useMemo(() => (
      entityList
        ? filter(entityList, (entity) => entity.name.toLowerCase().includes(searchQuery.toLowerCase()))
        : undefined
    ), [entityList, searchQuery]);

    const onSearchChange = (event) => {
      const newSearchText = event.target.value;
      setSearchText(newSearchText);
    };

    const debounceSearchQuery = debounce((_query) => {
      setSearchQuery(_query);
    }, DEBOUNCE_TIME);

    useEffect(() => {
      if (searchText !== searchQuery) {
        debounceSearchQuery(searchText);
      }
    }, [debounceSearchQuery, searchQuery, searchText]);

    const searchProps = {
      searchText,
      entityList: filteredEntityList,
      onSearchChange,
    };

    return <WrappedComponent {...rest} {...searchProps} />;
  };

  HOC.displayName = `withEntitySearch(${WrappedComponent.displayName || WrappedComponent.name})`;

  hoistNonReactStatics(HOC, WrappedComponent);

  return HOC;
};
